import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  Pie,
  PieChart,
  Cell,
  Legend,
  Tooltip,
  Sector,
} from "recharts";
import { PieSectorDataItem } from "recharts/types/polar/Pie";
import { ActiveShape } from "recharts/types/util/types";

const RADIAN = Math.PI / 180;

interface DataItem {
  name: string;
  value: number;
}

interface RenderCustomizedLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  index: any;
}

interface Props {
  data: DataItem[];
  titles?: string[];
}
const COLOURS = ["#1F77B4", "#FF7F0E", "#2CA02C", "#D62728", "#9467BD"];
export const LabelledPieChart = ({ data, titles }: Props) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 300,
    height: 300,
  });

  const massagedData = useMemo(() => {
    return data.map((d) => {
      const rounded = Math.round(d.value * 10) / 10;
      d.value = rounded;
      return d;
    });
  }, [data]);

  const handlePieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const handleResize = (width: number, height: number) => {
    setSize({ width, height });
  };

  const anyNonZero = massagedData.some((dataItem) => dataItem.value > 0);

  return (
    <>
      <div className="">
        {titles?.map((title) => (
          <p key={title} className="m-0">
            {title}
          </p>
        ))}
      </div>

      {/* Conditionally rendering the pie chart mitigates an active tool-tip render error if all
      data items change to zero. */}
      {anyNonZero && (
        <ResponsiveContainer height={400} onResize={handleResize}>
          <PieChart onClick={() => setActiveIndex(-1)}>
            <Pie
              height={300}
              data={massagedData}
              dataKey="value"
              cx="28%"
              cy="50%"
              radius={50}
              isAnimationActive={false}
              label={renderCustomizedLabel}
              labelLine={false}
              activeIndex={activeIndex}
              onClick={handlePieEnter}
              onMouseEnter={handlePieEnter}
              onMouseLeave={() => setActiveIndex(-1)}
              activeShape={renderActiveShape}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${entry.name}`}
                  fill={COLOURS[index % COLOURS.length]}
                />
              ))}
            </Pie>
            <Legend
              wrapperStyle={{
                maxWidth: "40%",
                top: "25%",
                right: 0,
                transform: "translateY(-50%)",
                position: "absolute",
              }}
              content={(p) => (
                <CustomLegend
                  {...p}
                  setActiveIndex={setActiveIndex}
                  small={size.width < 420}
                />
              )}
            />
            <Tooltip
              formatter={(value) => ` ${Math.round(Number(value))} Hd.`}
              wrapperClassName="p-1"
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </>
  );
};

const CustomLegend = (props: any) => {
  const enter = (idx: number) => () => props.setActiveIndex(idx);
  const leave = () => props.setActiveIndex(-1);

  const sorted = useMemo(() => {
    // move the items where the percent is 0 or undefined to the end of the array, leaving everything else untouched
    // we can opacity-0 those ones to hide them. display-none or removing them entirely causes the pie size to jump around
    return (props.payload ?? []).toSorted((a: any, b: any) => {
      const aValue = a.payload?.percent;
      const bValue = b.payload?.percent;

      if (
        (aValue === undefined || aValue === 0) &&
        bValue !== undefined &&
        bValue !== 0
      ) {
        return 1;
      }

      if (
        (bValue === undefined || bValue === 0) &&
        aValue !== undefined &&
        aValue !== 0
      ) {
        return -1;
      }
      return 0;
    });
  }, [props.payload]);
  return (
    <ul className="m-0 p-0">
      {sorted.map((entry: any, index: number) => {
        const percent = entry.payload?.percent;
        const { value: label, color } = entry;
        const mute = percent === undefined || percent <= 0;
        const formattedPercent = Math.round(percent * 1000) / 10;
        return (
          <li
            className={`list-group-item ${mute ? "opacity-0" : ""} py-0`}
            key={`${label}-${color}`}
            onMouseEnter={enter(index)}
            onMouseLeave={leave}
            onTouchStart={enter(index)}
            onTouchEnd={leave}
            onFocus={enter(index)}
            onBlur={leave}
            style={{ fontSize: props.small ? "13px" : "14px" }}
          >
            <div
              style={{
                backgroundColor: color,
                width: "1em",
                height: "1em",
                borderRadius: ".25em",
                display: "inline-block",
                marginBottom: "-0.2em",
              }}
            />
            <span>&nbsp;{label}&nbsp;</span>
            {isNaN(formattedPercent) ? null : (
              <strong>({formattedPercent}%)</strong>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}: RenderCustomizedLabelProps) => {
  if (percent < 0.05) return;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#f8f9fa"
      stroke="#343a40"
      strokeWidth={3}
      paintOrder={"stroke"}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      style={{ pointerEvents: "none" }}
      fontSize="1em"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const renderActiveShape: ActiveShape<PieSectorDataItem> = (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  );
};
